import React, { useState, useRef, useEffect } from "react";
import { uploadChunked } from "../lib/upload";
import { toast } from "./ui/sonner";
import { Loader2, Paperclip, Camera, X, Send, FileText, Users, Trash2, Plus, AlertTriangle } from "lucide-react";
import { useSmsDelivery, SmsDeliveryBadge } from "./SmsDelivery";
import { SmsConsentHint } from "./SmsConsentHint";

const isE164 = (v) => /^\+[1-9]\d{9,14}$/.test(v || "");

// Fullscreen overlay: user drags a rectangle on the captured screenshot to pick a region.
function RegionCropper({ src, zh, onCancel, onCrop }) {
  const imgRef = useRef();
  const [rect, setRect] = useState(null);
  const drag = useRef(null);
  const start = (e) => {
    const r = imgRef.current.getBoundingClientRect();
    drag.current = { x: e.clientX - r.left, y: e.clientY - r.top };
    setRect({ x: drag.current.x, y: drag.current.y, w: 0, h: 0 });
  };
  const move = (e) => {
    if (!drag.current) return;
    const r = imgRef.current.getBoundingClientRect();
    const cx = e.clientX - r.left, cy = e.clientY - r.top;
    setRect({ x: Math.min(drag.current.x, cx), y: Math.min(drag.current.y, cy), w: Math.abs(cx - drag.current.x), h: Math.abs(cy - drag.current.y) });
  };
  const end = () => { drag.current = null; };
  const confirm = () => {
    const img = imgRef.current;
    const scaleX = img.naturalWidth / img.clientWidth, scaleY = img.naturalHeight / img.clientHeight;
    const use = rect && rect.w > 5 && rect.h > 5 ? rect : { x: 0, y: 0, w: img.clientWidth, h: img.clientHeight };
    const c = document.createElement("canvas");
    c.width = Math.max(1, Math.round(use.w * scaleX)); c.height = Math.max(1, Math.round(use.h * scaleY));
    c.getContext("2d").drawImage(img, use.x * scaleX, use.y * scaleY, use.w * scaleX, use.h * scaleY, 0, 0, c.width, c.height);
    c.toBlob((b) => onCrop(b), "image/png");
  };
  return (
    <div className="fixed inset-0 z-[10001] bg-black/85 flex flex-col items-center justify-center p-4" data-testid="sms-region-cropper">
      <p className="text-white text-sm mb-2 text-center">{zh ? "拖动框选要发送的区域，或直接「确认使用」发送整张。" : "Drag to select a region, or use the whole image."}</p>
      <div className="relative max-w-full max-h-[72vh] overflow-auto rounded-lg">
        <img ref={imgRef} src={src} alt="capture" draggable={false}
          className="max-w-full max-h-[72vh] select-none cursor-crosshair"
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end} />
        {rect && <div className="absolute border-2 border-indigo-400 bg-indigo-400/20 pointer-events-none" style={{ left: rect.x, top: rect.y, width: rect.w, height: rect.h }} />}
      </div>
      <div className="flex gap-2 mt-3">
        <button onClick={onCancel} data-testid="sms-crop-cancel" className="px-4 py-2 rounded-lg bg-white/10 text-white text-sm hover:bg-white/20">{zh ? "取消" : "Cancel"}</button>
        <button onClick={confirm} data-testid="sms-crop-confirm" className="px-4 py-2 rounded-lg bg-brand-grad text-white font-semibold text-sm hover:opacity-90">{zh ? "确认使用" : "Use region"}</button>
      </div>
    </div>
  );
}

export default function SmsComposer({ api, zh, to, name, bulkList = [], setBulkList, fromNumber = "", onClearFrom }) {
  const [text, setText] = useState("");
  const [atts, setAtts] = useState([]); // {fileId, filename, contentType, preview}
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [capSrc, setCapSrc] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [showTpl, setShowTpl] = useState(false);
  const [manage, setManage] = useState(false);
  const [tplForm, setTplForm] = useState({ name: "", category: "报价", body: "" });
  const fileRef = useRef();
  const delivery = useSmsDelivery(api);

  useEffect(() => {
    api.get("/telephony/sms-templates").then(({ data }) => setTemplates(data.templates || [])).catch(() => {});
  }, [api]);

  const applyVars = (body) => (body || "").replace(/\{客户名\}/g, name || "").replace(/\{name\}/g, name || "");
  const insertTemplate = (t) => { setText((prev) => (prev ? prev + "\n" : "") + applyVars(t.body)); setShowTpl(false); };
  const saveTemplate = async () => {
    if (!tplForm.name.trim() || !tplForm.body.trim()) { toast.error(zh ? "请填写名称与内容" : "Name & body required"); return; }
    try {
      const { data } = await api.post("/telephony/sms-templates", tplForm);
      setTemplates((p) => [...p, data]); setTplForm({ name: "", category: "报价", body: "" });
      toast.success(zh ? "模板已保存" : "Saved");
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "保存失败" : "Failed")); }
  };
  const deleteTemplate = async (tid) => {
    try { await api.delete(`/telephony/sms-templates/${tid}`); setTemplates((p) => p.filter((x) => x.id !== tid)); } catch { /* */ }
  };
  const removeBulk = (i) => setBulkList && setBulkList((p) => p.filter((_, j) => j !== i));

  const uploadBlob = async (blob, filename, ct) => {
    const fin = await uploadChunked(api, blob, { name: filename, type: ct || "application/octet-stream" });
    return { fileId: fin.fileId, filename, contentType: ct || "application/octet-stream" };
  };

  const onPickFiles = async (e) => {
    const files = Array.from(e.target.files || []); if (!files.length) return;
    setBusy(true);
    try {
      for (const f of files) {
        const meta = await uploadBlob(f, f.name, f.type);
        setAtts((a) => [...a, { ...meta, preview: (f.type || "").startsWith("image/") ? URL.createObjectURL(f) : null }]);
      }
    } catch { toast.error(zh ? "上传失败" : "Upload failed"); }
    setBusy(false); if (fileRef.current) fileRef.current.value = "";
  };

  const startCapture = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
      toast.error(zh ? "当前浏览器不支持屏幕截图" : "Screen capture not supported");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
      const video = document.createElement("video");
      video.srcObject = stream;
      await video.play();
      await new Promise((r) => setTimeout(r, 350));
      const c = document.createElement("canvas");
      c.width = video.videoWidth || 1280; c.height = video.videoHeight || 720;
      c.getContext("2d").drawImage(video, 0, 0, c.width, c.height);
      stream.getTracks().forEach((t) => t.stop());
      setCapSrc(c.toDataURL("image/png"));
    } catch {
      toast.error(zh ? "截屏已取消或被拒绝" : "Capture cancelled");
    }
  };

  const onCropped = async (blob) => {
    setCapSrc(null);
    if (!blob) return;
    setBusy(true);
    try {
      const meta = await uploadBlob(blob, `screenshot_${Date.now()}.png`, "image/png");
      setAtts((a) => [...a, { ...meta, preview: URL.createObjectURL(blob) }]);
      toast.success(zh ? "截图已添加" : "Screenshot added");
    } catch { toast.error(zh ? "截图上传失败" : "Upload failed"); }
    setBusy(false);
  };

  const removeAtt = (i) => setAtts((a) => a.filter((_, idx) => idx !== i));

  const send = async () => {
    if (!text.trim() && !atts.length) return;
    setSending(true);
    try {
      if ((bulkList || []).length > 0) {
        const { data } = await api.post("/telephony/sms-bulk", { recipients: bulkList, message: text, fileIds: atts.map((a) => a.fileId) });
        if (data.ok) {
          const failed = (data.results || []).filter((r) => !r.ok).length;
          toast.success(zh ? `群发完成：成功 ${data.sent}/${data.total}${failed ? `，失败 ${failed}` : ""}` : `Bulk sent ${data.sent}/${data.total}`);
          setText(""); setAtts([]); setBulkList && setBulkList([]);
        } else { toast.error(zh ? "群发失败" : "Bulk failed"); }
      } else {
        if (!isE164(to)) { toast.error(zh ? "收件号码无效：请在上方输入 10 位美国号码（如 6265551234）或 +国家码 格式" : "Enter a valid recipient (+1 and 10 digits)"); setSending(false); return; }
        delivery.clear();
        const { data } = await api.post("/telephony/sms-rich", { to, message: text, fileIds: atts.map((a) => a.fileId), ...(fromNumber ? { fromNumber } : {}) });
        if (data.ok) {
          toast.success(zh ? `短信已提交${data.mms ? `（含 ${data.mms} 张图片）` : ""}${data.links ? `（${data.links} 个附件链接）` : ""}，正在等待运营商投递回执…` : "SMS submitted");
          setText(""); setAtts([]); delivery.track(data.info);
        } else { toast.error(data.hint || (zh ? "发送失败" : "Send failed"), { duration: 8000 }); }
      }
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "发送失败（网络或服务器错误）" : "Failed"), { duration: 8000 }); }
    setSending(false);
  };

  const bulk = (bulkList || []).length;
  const badTo = bulk === 0 && !!to && !isE164(to);
  return (
    <div className="space-y-2" data-testid="sms-composer">
      {capSrc && <RegionCropper src={capSrc} zh={zh} onCancel={() => setCapSrc(null)} onCrop={onCropped} />}
      {fromNumber && (
        <div className="flex items-center gap-1 text-[11px] text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-md px-2 py-1" data-testid="sms-from-number">
          {zh ? "以来信号码发送" : "Send from"} <span className="font-mono font-semibold">{fromNumber}</span>
          <button type="button" data-testid="sms-from-number-clear" onClick={onClearFrom} className="ml-auto text-slate-400 hover:text-slate-700" title={zh ? "改用默认发送号" : "Use default sender"}><X className="h-3 w-3" /></button>
        </div>
      )}

      {bulk > 0 ? (
        <div className="rounded-lg bg-indigo-50 border border-indigo-100 p-2" data-testid="sms-bulk-list">
          <p className="text-[11px] text-indigo-700 font-semibold mb-1 flex items-center gap-1"><Users className="h-3 w-3" />{zh ? `群发列表（${bulk} 人）` : `Bulk (${bulk})`}</p>
          <div className="flex flex-wrap gap-1">
            {bulkList.map((r, i) => (
              <span key={i} data-testid={`sms-bulk-chip-${i}`} className="inline-flex items-center gap-1 text-[11px] bg-white border border-slate-200 text-slate-700 rounded-full pl-2 pr-1 py-0.5">
                {r.name ? `${r.name} ` : ""}{r.phone}
                <button onClick={() => removeBulk(i)} data-testid={`sms-bulk-remove-${i}`} className="h-5 w-5 rounded-full bg-red-500/70 hover:bg-red-500 text-white flex items-center justify-center"><X className="h-3 w-3 pointer-events-none" /></button>
              </span>
            ))}
          </div>
        </div>
      ) : (
        <div className="text-[11px] text-slate-500">
          {zh ? "发送至" : "To"}: <span className={`font-semibold ${badTo ? "text-red-600" : "text-indigo-700"}`}>{name ? `${name} · ` : ""}{to || (zh ? "（未填号码，可从上方搜索选择或加入群发）" : "(no number)")}</span>
          {badTo && <span className="block text-red-600 mt-0.5 inline-flex items-center gap-1" data-testid="sms-to-invalid"><AlertTriangle className="h-3 w-3" />{zh ? "号码格式无效：美国号码请输入 10 位数字或 +1 开头" : "Invalid number: use 10 digits or +1…"}</span>}
        </div>
      )}

      {bulk === 0 && to && isE164(to) && <SmsConsentHint api={api} zh={zh} phone={to} testid="sms-composer-consent-hint" />}

      <div className="relative">
        <button onClick={() => setShowTpl((v) => !v)} data-testid="sms-template-btn" className="text-[11px] text-indigo-700 border border-indigo-200 bg-indigo-50/60 rounded-md px-2 py-1 hover:bg-indigo-100 inline-flex items-center gap-1"><FileText className="h-3 w-3" />{zh ? "短信模板" : "Templates"}</button>
        {showTpl && (
          <div className="absolute left-0 right-0 mt-1 z-10 rounded-lg border border-slate-200 bg-white shadow-xl p-2 space-y-1 max-h-64 overflow-y-auto" data-testid="sms-template-list">
            {templates.length === 0 && <p className="text-[11px] text-slate-500 px-1">{zh ? "暂无模板" : "No templates"}</p>}
            {templates.map((t) => (
              <div key={t.id} className="flex items-center gap-1 group">
                <button onClick={() => insertTemplate(t)} data-testid={`sms-template-${t.id}`} className="text-left flex-1 min-w-0 px-2 py-1 rounded hover:bg-indigo-50">
                  <span className="text-[11px] text-indigo-600">[{t.category}]</span> <span className="text-[12px] text-slate-800">{t.name}</span>
                  <span className="block text-[10px] text-slate-400 truncate">{t.body}</span>
                </button>
                <button onClick={() => deleteTemplate(t.id)} data-testid={`sms-template-del-${t.id}`} className="shrink-0 text-slate-400 hover:text-red-600 p-1"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
            <button onClick={() => setManage((v) => !v)} data-testid="sms-template-manage" className="w-full text-[11px] text-slate-600 hover:text-indigo-700 border-t border-slate-100 pt-1.5 mt-1 flex items-center justify-center gap-1"><Plus className="h-3 w-3" />{zh ? "新增模板" : "New template"}</button>
            {manage && (
              <div className="space-y-1 pt-1" data-testid="sms-template-form">
                <div className="flex gap-1">
                  <select value={tplForm.category} onChange={(e) => setTplForm((f) => ({ ...f, category: e.target.value }))} className="text-[11px] bg-slate-50 border border-slate-200 rounded px-1 py-1 text-slate-800">
                    {["报价", "预约", "回访", "其它"].map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <input value={tplForm.name} onChange={(e) => setTplForm((f) => ({ ...f, name: e.target.value }))} data-testid="sms-template-name" placeholder={zh ? "模板名称" : "Name"} className="flex-1 text-[11px] bg-slate-50 border border-slate-200 rounded px-2 py-1 text-slate-900" />
                </div>
                <textarea value={tplForm.body} onChange={(e) => setTplForm((f) => ({ ...f, body: e.target.value }))} data-testid="sms-template-body" rows={2} placeholder={zh ? "内容，可用变量 {客户名} {地址}" : "Body, vars {客户名} {地址}"} className="w-full text-[11px] bg-slate-50 border border-slate-200 rounded px-2 py-1 text-slate-900" />
                <button onClick={saveTemplate} data-testid="sms-template-save" className="w-full text-[11px] bg-brand-grad text-white font-semibold rounded py-1">{zh ? "保存模板" : "Save"}</button>
              </div>
            )}
          </div>
        )}
      </div>

      <textarea data-testid="sms-text" value={text} onChange={(e) => setText(e.target.value)} rows={3}
        placeholder={zh ? "输入短信内容…" : "Type your message…"}
        className="w-full rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-slate-900 placeholder:text-slate-400 text-sm focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100" />


      {atts.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="sms-attachments">
          {atts.map((a, i) => (
            <div key={a.fileId} className="relative" data-testid={`sms-att-${i}`}>
              {a.preview
                ? <img src={a.preview} alt={a.filename} className="h-14 w-14 object-cover rounded-lg border border-slate-200" />
                : <div className="h-14 w-14 rounded-lg border border-slate-200 bg-slate-50 flex flex-col items-center justify-center text-[9px] text-slate-600 p-1"><FileText className="h-4 w-4 mb-0.5" /><span className="truncate w-full text-center">{(a.filename || "file").split(".").pop().toUpperCase()}</span></div>}
              <button onClick={() => removeAtt(i)} data-testid={`sms-att-remove-${i}`} className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-red-600 text-white flex items-center justify-center"><X className="h-2.5 w-2.5" /></button>
            </div>
          ))}
        </div>
      )}

      <input ref={fileRef} type="file" multiple className="hidden" data-testid="sms-file-input" onChange={onPickFiles} accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx" />
      <div className="flex items-center gap-1.5">
        <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="sms-attach-btn"
          className="h-9 px-2.5 rounded-lg bg-slate-50 border border-slate-200 hover:bg-slate-100 text-xs text-slate-700 flex items-center gap-1 disabled:opacity-50">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5" />}{zh ? "附件" : "Attach"}
        </button>
        <button onClick={startCapture} disabled={busy} data-testid="sms-screenshot-btn"
          className="h-9 px-2.5 rounded-lg bg-slate-50 border border-slate-200 hover:bg-slate-100 text-xs text-slate-700 flex items-center gap-1 disabled:opacity-50">
          <Camera className="h-3.5 w-3.5" />{zh ? "截图" : "Capture"}
        </button>
        <button onClick={send} disabled={sending || badTo || (!text.trim() && !atts.length)} data-testid="sms-send-btn" title={badTo ? (zh ? "收件号码无效" : "Invalid number") : (!text.trim() && !atts.length) ? (zh ? "请先输入短信内容或添加附件" : "Type a message") : ""}
          className="flex-1 h-9 rounded-lg bg-brand-grad hover:opacity-90 disabled:opacity-40 text-white font-bold flex items-center justify-center gap-1.5 text-sm">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}{bulk > 0 ? (zh ? `群发 ${bulk} 人` : `Send to ${bulk}`) : (zh ? "发送短信" : "Send SMS")}
        </button>
      </div>
      <SmsDeliveryBadge st={delivery.st} zh={zh} />
      <p className="text-[10px] text-slate-400">{zh ? "图片作为彩信直接发送；PDF/文件以下载链接随短信附带。" : "Images sent as MMS; PDFs/files sent as download links."}</p>
    </div>
  );
}
